import { defaultHeaders } from '..'
import { csrf } from '../csrf'
import {
  ApiError,
  errorCodeFrom,
  errorEntryText,
  messageForError,
  readErrorPayload,
} from './api-error'
import {
  reportAgentSupportLock,
  SUPPORT_SESSION_LOCKED_STATUS,
} from '@/lib/agent-support-lock'

export { ApiError } from './api-error'

export interface APIRequestConfig {
  errorPrefix?: string
  handleValidationErrors?: boolean
  throwOnValidationErrors?: boolean
}

export interface ValidationErrorResponse {
  errors: string[]
  isValidationError: true
}

const normalizeValidationErrors = (errorData: {
  errors?: unknown
  error?: unknown
}): string[] => {
  const { errors, error } = errorData
  if (Array.isArray(errors)) {
    return errors.map(errorEntryText)
  }
  if (errors && typeof errors === 'object') {
    return Object.entries(errors as Record<string, unknown>).flatMap(
      ([field, messages]) => {
        const list = Array.isArray(messages) ? messages : [messages]
        return list.map((message) => `${field}: ${message}`)
      }
    )
  }
  if (typeof error === 'string' && error.length > 0) {
    return [error]
  }
  return ['Validation failed']
}

const isFieldErrorMap = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const fieldErrorsFrom = (errorData: {
  errors?: unknown
  fieldErrors?: unknown
}): Record<string, string[]> | undefined => {
  const { errors, fieldErrors } = errorData
  let fieldErrorMap: Record<string, unknown>
  if (isFieldErrorMap(errors)) {
    fieldErrorMap = errors
  } else if (isFieldErrorMap(fieldErrors)) {
    fieldErrorMap = fieldErrors
  } else {
    return undefined
  }

  return Object.fromEntries(
    Object.entries(fieldErrorMap).map(([field, messages]) => [
      field,
      (Array.isArray(messages) ? messages : [messages]).map(String),
    ])
  )
}

export const apiRequest = async <T>(
  endpoint: string,
  options: RequestInit = {},
  config: APIRequestConfig = {}
): Promise<T> => {
  const { errorPrefix = 'API request failed', handleValidationErrors = false } =
    config

  const response = await fetch(endpoint, {
    headers: defaultHeaders,
    ...options,
  })

  if (!response.ok) {
    const errorData = await readErrorPayload(response)

    if (response.status === SUPPORT_SESSION_LOCKED_STATUS) {
      reportAgentSupportLock()
      throw new ApiError(
        messageForError(errorData, response, errorPrefix),
        response.status,
        response.statusText
      )
    }

    if (response.status === 422) {
      console.error(errorData)
      const validationErrors = normalizeValidationErrors(errorData)
      if (config.throwOnValidationErrors) {
        throw new ApiError(
          validationErrors.join(', '),
          response.status,
          response.statusText,
          validationErrors,
          fieldErrorsFrom(errorData),
          errorCodeFrom(errorData)
        )
      } else {
        return {
          errors: validationErrors,
          isValidationError: true,
        } as any
      }
    }

    throw new ApiError(
      messageForError(errorData, response, errorPrefix),
      response.status,
      response.statusText,
      undefined,
      undefined,
      errorCodeFrom(errorData)
    )
  }

  if (response.status === 204) {
    return {} as T
  }

  return response.json()
}

export const queryRequest = async <T>(
  endpoint: string,
  options: RequestInit = {},
  errorPrefix?: string
): Promise<T> => {
  return apiRequest<T>(endpoint, options, {
    errorPrefix,
    handleValidationErrors: false,
  })
}

export const mutationRequest = async <T>(
  endpoint: string,
  options: RequestInit = {},
  errorPrefix?: string,
  throwOnValidationErrors: boolean = false
): Promise<T> => {
  return apiRequest<T>(endpoint, options, {
    errorPrefix,
    handleValidationErrors: true,
    throwOnValidationErrors,
  })
}

export const formDataMutationRequest = async <T>(
  endpoint: string,
  options: RequestInit = {},
  errorPrefix?: string
): Promise<T> => {
  const prefix = errorPrefix || 'API request failed'

  const response = await fetch(endpoint, {
    ...options,
    headers: {
      'X-CSRF-Token': csrf,
      Accept: 'application/json',
    },
  })

  if (!response.ok) {
    const errorData = await readErrorPayload(response)

    if (response.status === SUPPORT_SESSION_LOCKED_STATUS) {
      reportAgentSupportLock()
      throw new ApiError(
        messageForError(errorData, response, prefix),
        response.status,
        response.statusText
      )
    }

    if (response.status === 422) {
      console.error(errorData)
      return {
        errors: normalizeValidationErrors(errorData),
        isValidationError: true,
      } as any
    }

    throw new ApiError(
      messageForError(errorData, response, prefix),
      response.status,
      response.statusText
    )
  }

  return response.json()
}
